import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { I18nModule, TranslationService } from '@spartacus/core';
import { LocalLoaderComponent } from '../../../../../components/loader/local-loader/local-loader.component';
import { AlertComponent } from '../../../../../components/alert/alert.component';
import { TableProComponent } from '../../../../../components/table-pro/table-pro.component';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Option } from '../../../../../core/models/option.model';
import {
  PaymentHistoryItem,
  PaymentHistoryReportRequest,
  PaymentHistoryRequestConfig,
  PH_ATTRIBUTES_TYPE,
  PH_SEARCH_TYPE,
} from '../../_core/models/payment-history.model';
import { FormErrorsModule } from '@spartacus/storefront';
import { BehaviorSubject, Subscription, take, firstValueFrom } from 'rxjs';
import { CustomValidatorService } from '../../../../../components/form-elements/custom-validator.service';
import { initialStatus, loadingStatus } from '../../../../../core/models/request-status.model';
import { Alert, ALERT_TYPE } from '../../../../../components/alert/alert.model';
import { TableProConfig } from '../../../../../components/table-pro/core/table-pro.model';
import { ModalService } from '../../../../../components/modal-list/modal-list.service';
import { PaymentHistoryDetailsComponent } from './payment-history-details/payment-history-details.component';
import { ReportsService } from '../../_core/reports.service';

@Component({
  selector: 'payment-history',
  standalone: true,
  imports: [
    CommonModule,
    I18nModule,
    ReactiveFormsModule,
    LocalLoaderComponent,
    AlertComponent,
    TableProComponent,
    FormErrorsModule,
  ],
  templateUrl: './payment-history.component.html',
  styleUrl: './payment-history.component.scss',
})
export class PaymentHistoryComponent implements OnInit, OnDestroy {
  PH_ATTRIBUTES_TYPE = PH_ATTRIBUTES_TYPE;
  PH_SEARCH_TYPE = PH_SEARCH_TYPE;

  report$ = new BehaviorSubject<PaymentHistoryReportRequest>(initialStatus);
  placeholder = 'Enter Payment Number';
  subscription = new Subscription();

  alertDanger: Alert = {
    isActive: true,
    title: '',
    content: '',
    containerClass: 'm-t-20',
    hasCloseButton: false,
    type: ALERT_TYPE.DANGER,
  };

  tableConfig: TableProConfig = {
    classContainer: 'm-t-20',
    itemsPerPage: 50,
    collumnConfig: [
      { id: 'yourPayment', index: 0, type: 'text' },
      { id: 'receiptDate', index: 1, type: 'date' },
      { id: 'paymentAmount', index: 2, type: 'number' },
      { id: 'appliedAmount', index: 3, type: 'number' },
    ],
  };

  form: FormGroup = this.fb.group({
    attributeType: [PH_ATTRIBUTES_TYPE.PAYMENT_NUMBER, []],
    searchType: [PH_SEARCH_TYPE.START_WITH, []],
    value: ['', []],
  });

  attributesType: Option[] = [
    {
      code: PH_ATTRIBUTES_TYPE.PAYMENT_NUMBER,
      label: 'Payment Number',
    },
    {
      code: PH_ATTRIBUTES_TYPE.AMOUNT,
      label: 'Amount',
    },
  ];

  searchType: Option[] = [
    {
      code: PH_SEARCH_TYPE.EXACTLY,
      label: 'Is Exactly',
    },
    {
      code: PH_SEARCH_TYPE.START_WITH,
      label: 'Start With',
    },
  ];

  constructor(
    protected fb: FormBuilder,
    private reportsService: ReportsService,
    private customValidatorService: CustomValidatorService,
    private modalService: ModalService,
    private translationService: TranslationService,
  ) {}

  ngOnInit(): void {
    this.listnerSearchBy();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  listnerSearchBy() {
    this.subscription.add(
      this.translationService
        .translate('generic.error')
        .pipe(take(1))
        .subscribe(value => {
          this.alertDanger.content = value;
        }),
    );

    this.subscription.add(
      this.form.get('attributeType')?.valueChanges.subscribe(async value => {
        this.placeholder = await firstValueFrom(
          this.translationService.translate(
            value === PH_ATTRIBUTES_TYPE.PAYMENT_NUMBER
              ? 'paymentHistory.enterPaymentNumber'
              : 'paymentHistory.enterAmount'
          )
        );
      }),
    );
  }

  async openPaymentHistoryDetails(item: PaymentHistoryItem) {
    const title = await firstValueFrom(this.translationService.translate('paymentHistory.paymentHistoryDetails'));
    this.modalService.create({
      id: 'payment-history-details',
      title,
      width: 900,
      component: PaymentHistoryDetailsComponent,
      data: { item },
    });
  }

  getPaymetHistoryReport() {
    this.report$.next(loadingStatus);

    const config: PaymentHistoryRequestConfig = {
      attributesType: this.form.get('attributeType')?.value,
      searchType: this.form.get('searchType')?.value,
      value: this.form.get('value')?.value,
    };

    this.subscription.add(
      this.reportsService
        .getPaymentHistory(config)
        .pipe(take(1))
        .subscribe(_report => {
          const report: PaymentHistoryReportRequest = {
            ..._report,
          };

          if (report.hasError) {
            this.alertDanger.content = report.message!;
          }

          this.report$.next(report);
        }),
    );
  }

  submit() {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.getPaymetHistoryReport();
    }
  }
}